import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";

// Updated to fetch from API instead of static JSON
export const getHistoricalInvoices = async () => {
  try {
    // This should be replaced with an actual API call when available
    const baseUrl = `${VITE_API_V2_URL}/`;
    const response = await fetch(`${baseUrl}dashboard/historical-invoices`);
    const data = await response.json();
    return {
      historicalData: Array.isArray(data) ? data : data?.data || [],
    };
  } catch (error) {
    console.error("Error fetching historical invoices:", error);
    // Return empty array as fallback
    return { historicalData: [] };
  }
};

interface TransactionLadgerQueryParams {
  userId: string;
  page?: number;
  per_page?: number;
  search?: string;
  type?: string;
  funder_id?: string;
}

interface TransactionLadgerResponse {
  data: {
    transactions: any[];
    pagination: {
      current_page: number;
      last_page: number;
      total: number;
      per_page: number;
    };
  };
}

export const investor_v2 = createApi({
  reducerPath: "investor_v2",
  tagTypes: ["TABLE_VALUES", "TRANSACTION_LADGER", "AUM_DETAILS"],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/`,
  }),
  endpoints: (builder) => ({
    GetInvoice: builder.query<any, { endpoint: string }>({
      query: (params) => {
        const cleanedEndpoint = params.endpoint.startsWith("/")
          ? params.endpoint.substring(1)
          : params.endpoint;
        return {
          url: `${cleanedEndpoint}`,
          method: "get",
        };
      },
      providesTags: () => ["TABLE_VALUES"],
      transformResponse: (response) => {
        if (!response) {
          return {
            invoices: [],
            funders: [],
            total_cash_available: 0,
          };
        }

        if (response.invoices !== undefined) {
          return {
            invoices: Array.isArray(response.invoices) ? response.invoices : [],
            funders: Array.isArray(response.funders) ? response.funders : [],
            total_cash_available: response.total_cash_available || 0,
          };
        }

        if (response.data) {
          return {
            invoices: Array.isArray(response.data.invoices)
              ? response.data.invoices
              : [],
            funders: Array.isArray(response.data.funders)
              ? response.data.funders
              : [],
            total_cash_available: response.data.total_cash_available || 0,
          };
        }

        if (response.data && Array.isArray(response.data)) {
          return {
            invoices: response.data,
            funders: [],
            total_cash_available: response.total_cash_available || 0,
          };
        }

        return {
          invoices: [],
          funders: [],
          total_cash_available: 0,
        };
      },
    }),
    GetInvestorsSummary: builder.query<any, { year?: string }>({
      query: (params) => {
        const { year = "all" } = params;
        return {
          url: `dashboard/investors-summary?year=${year}`,
          method: "get",
        };
      },
      providesTags: () => ["TABLE_VALUES"],
      transformResponse: (response) => {
        if (!response) {
          return {
            invoices: [],
            funders: [],
            total_cash_available: 0,
          };
        }

        if (response.data) {
          return response.data;
        }

        return response;
      },
    }),
    GetTransactionLadger: builder.query<any, { transparams: any }>({
      query: ({ transparams }) => {
        return {
          url: `dashboard/transactions${transparams}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response?.data,
      providesTags: ["TRANSACTION_LADGER"],
    }),
    GetAUMDetails: builder.query<any, string>({
      query: (currency) => ({
        url: `aum/currencies/${currency}`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
      providesTags: ["AUM_DETAILS"],
    }),

    GetImporters: builder.query<any, any>({
      query: (accountType) => ({
        url: `dashboard/${accountType}`,
        method: "get",
      }),
      providesTags: () => ["TRANSACTION_LADGER"],
      transformResponse: (response) => {
        return response?.data;
      },
    }),
    GetSector: builder.query<any, void>({
      query: () => ({
        url: `dashboard/by/industry`,
        method: "get",
      }),
      providesTags: () => ["TRANSACTION_LADGER"],
      transformResponse: (response) => {
        return response?.data;
      },
    }),
  }),
});

export const {
  useGetInvoiceQuery,
  useGetInvestorsSummaryQuery,
  useGetTransactionLadgerQuery,
  useGetImportersQuery,
  useGetAUMDetailsQuery,
  useGetSectorQuery,
} = investor_v2;
